import { onMounted, nextTick, reactive, ref, watch } from 'vue'
import { useInfiniteScroll } from '@vueuse/core'
import { axiosInstanceWithAuth } from '@/utils/utils'
import useMonitoring from '@/services/composables/useMonitoring'
import { getPunctualMonitoringColor } from '@/utils'

export function useHomeMonitorings({
    uxStore,
    itemList,
    monitorings: externalMonitorings,
    closeExpanded,
    afterDataUpdate,
}) {
    const loading = ref(true)
    const monitorings = externalMonitorings || reactive({ data: [] })

    const pageCounter = ref(1)
    const changedFilter = ref(false)

    const topographicElementsTypes = ref([])
    const users = ref([])
    const projects = ref([])

    const selectedTopographicElement4Type = ref(null)
    const selectedMonitoringType = ref(null)
    const selectedTimeRange = ref(null)
    const selectedCompiler = ref(null)
    const selectedProject = ref(null)
    const orderBy = ref(null)

    const searchQuery = ref('')
    let searchTimeout = null

    const punctualMonitoringFilters = reactive({
        type: '',
        level: { 1: false, 2: false, 3: false, 4: false, 5: false },
    })

    const setRightMenuPolygons = () => {
        uxStore.rightMenu.data.polygons = (monitorings.data || [])
            .map(item => {
                if (!item.topographic_element4) return null
                const color = item.monitoring_type === 'punctual'
                    ? getPunctualMonitoringColor(item)
                    : '#' + item.color
                return {
                    style: { color },
                    tooltip:
                        item.topographic_element4?.name +
                        '<br>Regione: ' +
                        item.topographic_element4?.regionName +
                        '<br>Insula: ' +
                        item.topographic_element4?.insulaName +
                        '<br>Unità: ' +
                        item.topographic_element4?.unityName +
                        '<br>Ambiente: ' +
                        item.topographic_element4?.environmentName,
                    ...item.topographic_element4,
                }
            })
            .filter(i => i && i.polygon)
    }

    const buildSearchParams = (page) => ({
        query: searchQuery.value,
        page,
        topographicElement4TypeId: selectedTopographicElement4Type.value,
        compilerId: selectedCompiler.value,
        projectId: selectedProject.value,
        creationDate: selectedTimeRange.value,
        monitoringType: selectedMonitoringType.value,
        orderBy: orderBy.value,
        punctualMonitoringFilters,
    })

    function maybeLoadMoreIfNearBottom() {
        if (!itemList?.value) return

        const distance = 200
        const remaining =
            itemList.value.scrollHeight - itemList.value.scrollTop - itemList.value.clientHeight

        if (remaining <= distance) {
            loadNextPage()
        }
    }

    const updateMonitorings = (customListener = null) => {
        loading.value = true

        if (changedFilter.value) {
            pageCounter.value = 1
            changedFilter.value = false

            if (itemList?.value) itemList.value.scrollTop = 0
            monitorings.data = []
            closeExpanded?.()
        }

        axiosInstanceWithAuth
            .get('/api/bff/monitoring/search', {
                params: buildSearchParams(pageCounter.value),
            })
            .then(res => {
                if (customListener) {
                    customListener(res.data)
                } else {
                    monitorings.data = res.data.data
                    nextTick(maybeLoadMoreIfNearBottom)
                }

                setRightMenuPolygons()
                loading.value = false

                afterDataUpdate?.()
            })
    }

    function loadNextPage() {
        if (loading.value || refreshInFlight.value) return

        pageCounter.value++

        updateMonitorings(data => {
            const items = data?.data || []

            if (items.length === 0) {
                pageCounter.value--
                return
            }

            monitorings.data = [...monitorings.data, ...items]
            nextTick(maybeLoadMoreIfNearBottom)
            afterDataUpdate?.()
        })
    }

    useInfiniteScroll(itemList, loadNextPage, { distance: 200 })

    const refreshInFlight = ref(false)
    const refreshRunId = ref(0)

    const refreshAllPages = async () => {
        if (refreshInFlight.value) return

        const runId = ++refreshRunId.value
        refreshInFlight.value = true
        loading.value = true

        const aggregatedData = []

        try {
            for (let p = 1; p <= pageCounter.value; p++) {
                const res = await axiosInstanceWithAuth.get('/api/bff/monitoring/search', {
                    params: buildSearchParams(p),
                })
                aggregatedData.push(...res.data.data)
            }

            if (runId !== refreshRunId.value) return

            monitorings.data = aggregatedData
            setRightMenuPolygons()
            afterDataUpdate?.()
        } finally {
            if (runId === refreshRunId.value) {
                refreshInFlight.value = false
                loading.value = false
            }
        }
    }

    // Filters
    const filterByTe4Type = (te4Type) => {
        changedFilter.value = true
        selectedTopographicElement4Type.value = te4Type.value
        updateMonitorings()
    }

    const filterByCompiler = (compiler) => {
        changedFilter.value = true
        selectedCompiler.value = compiler.value
        updateMonitorings()
    }

    const filterByProject = (project) => {
        changedFilter.value = true
        selectedProject.value = project.value
        updateMonitorings()
    }

    const filterByCreationDate = (date) => {
        changedFilter.value = true
        selectedTimeRange.value = date.value
        updateMonitorings()
    }

    const selectMonitoringType = (type) => {
        changedFilter.value = true
        selectedMonitoringType.value = type.value
        updateMonitorings()
    }

    const clearMonitorings = async () => {
        const useMonitorings = useMonitoring('punctual-monitoring')
        await useMonitorings.clearDataToSync('punctual-monitoring')
    }

    const search = () => {
        changedFilter.value = true
        clearTimeout(searchTimeout)
        searchTimeout = setTimeout(() => {
            updateMonitorings()
        }, 1000)
    }

    watch(
        () => punctualMonitoringFilters,
        () => {
            changedFilter.value = true
            search()
        },
        { deep: true }
    )

    onMounted(async () => {
        axiosInstanceWithAuth.get('/api/topographic-element-4-type').then(res => {
            topographicElementsTypes.value = res.data.data.map(type => ({ label: type.name, value: type.id }))
            topographicElementsTypes.value.unshift({ label: 'Referente topografico', value: null })
            filterByTe4Type(topographicElementsTypes.value[0])
        })

        axiosInstanceWithAuth.get('/api/users').then(res => {
            users.value = res.data
                .map(user => ({ label: user.name + ' ' + user.surname, value: user.id }))
                .sort((a, b) => (a.label < b.label ? -1 : 1))
        })

        axiosInstanceWithAuth.get('/api/project').then(res => {
            projects.value = res.data.data.map(project => ({ label: project.name, value: project.id }))
        })
    })

    return {
        loading,
        monitorings,
        pageCounter,
        changedFilter,

        topographicElementsTypes,
        users,
        projects,

        selectedTopographicElement4Type,
        selectedMonitoringType,
        selectedTimeRange,
        selectedCompiler,
        selectedProject,
        orderBy,

        searchQuery,
        punctualMonitoringFilters,

        updateMonitorings,
        refreshAllPages,
        filterByTe4Type,
        filterByCompiler,
        filterByProject,
        filterByCreationDate,
        selectMonitoringType,
        clearMonitorings,
        search,
    }
}
